import { Button, ButtonGroup } from '@mantine/core';
import { sizes, variants } from '@uag/react-core';
import { useTranslation } from 'react-i18next';

import { SettingModelSettingType } from 'api/models';
import { SolverCostWeightsMenu } from 'app/modules/wallforming/components/setting-container/automated-placement/SolverCostWeightsMenu';
import 'app/modules/wallforming/custom-styles/customButtonStyle.css';

export const placementSettingTypes = [
    SettingModelSettingType.Corners,
    SettingModelSettingType.StopEnd,
    SettingModelSettingType.Panels,
    SettingModelSettingType.Connectors,
    SettingModelSettingType.Anchors
];

interface PlacementTypeHeaderProps {
    availableSettingTypes: SettingModelSettingType[];
    selectedSettingType: SettingModelSettingType;
    onSelectSettingType: (settingType: SettingModelSettingType) => void;
    actionNode: React.ReactNode;
    isLoading: boolean;
}

export const PlacementTypeHeader = ({
    availableSettingTypes,
    selectedSettingType,
    onSelectSettingType,
    actionNode,
    isLoading
}: PlacementTypeHeaderProps) => {
    const { t } = useTranslation();

    return (
        <div className="border-b border-border-ultra-light p-2 pr-3">
            <div className="mb-1 flex items-start justify-between gap-2">
                <div className="min-w-0 flex-1">
                    <div className="text-sm font-semibold uppercase tracking-wide">{t('labelPlacementType')}</div>
                </div>

                <div className="shrink-0">
                    <SolverCostWeightsMenu settingType={selectedSettingType} />
                </div>
            </div>

            <div className="flex min-w-0">
                <ButtonGroup
                    className="automated-placement-type-button-group min-w-0 max-w-full"
                    style={{ flexWrap: 'wrap' }}
                    variant={variants.buttonGroup.small}
                >
                    {placementSettingTypes.map((settingType, index) => (
                        <Button
                            key={settingType}
                            aria-pressed={
                                selectedSettingType === settingType && availableSettingTypes.includes(settingType)
                            }
                            className="automated-placement-type-button m-0! h-auto flex-1 justify-center min-w-24! max-[1440px]:text-xs"
                            classNames={{
                                inner: 'h-full items-center justify-center',
                                label: 'flex h-full w-full items-center justify-center text-center leading-none'
                            }}
                            data-selected={selectedSettingType === settingType}
                            disabled={isLoading || !availableSettingTypes.includes(settingType)}
                            size={sizes.button.small}
                            variant={variants.button.secondary}
                            onClick={() => onSelectSettingType(settingType)}
                        >
                            <div className="flex flex-col items-center leading-none">
                                <span className="font-bold mb-0.5">{index + 1}</span>
                                <div className="flex flex-row">{settingType}</div>
                            </div>
                        </Button>
                    ))}
                </ButtonGroup>
            </div>

            {actionNode}

            {isLoading && (
                <div className="mt-2 text-xs text-text-gray-dark">{t('labelLoadingAutomatedPlacementTypes')}</div>
            )}
        </div>
    );
};
